import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Dialog } from "primereact/dialog";
import ComissaoService from "../../services/resource/ComissaoService";
import { CustomTable } from "../../componentes/table/CustomTable";

const fmtDia = (iso) => {
    if (!iso) return "—";
    const [, m, d] = iso.split("-");
    return `${d}/${m}`;
};

const fmtData = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

const fmtNumero = (n) => Number(n ?? 0).toLocaleString("pt-BR");

const SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const FIM_DE_SEMANA = ["sáb", "dom"];

// sigla de cada relatório dentro da célula do dia
const SIGLA = {
    ESCALAS: "Esc",
    GREENMILE: "GM",
    SAP_ENTREGAS: "Ent",
    SAP_RETORNO: "Ret",
    PEDIDOS_DA: "Ped",
};

// dia da semana (0 = domingo) de uma data ISO, sem depender de fuso horário
const indiceSemana = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).getDay();
};

// DA tem uma fonte só (pedidos): dia útil sem pedidos conta como pendência; no DL a pendência vem do backend (semDados)
const estaPendente = (autorizado, dia, codigo) => autorizado
    ? !FIM_DE_SEMANA.includes(dia.diaSemana) && (dia.quantidades[codigo] ?? 0) === 0
    : Boolean(dia.semDados?.includes(codigo));

/** Quebra a lista de dias em semanas (domingo a sábado), completando as pontas com vazio. */
function montarSemanas(dias) {
    if (!dias.length) return [];
    const celulas = [...Array(indiceSemana(dias[0].data)).fill(null), ...dias];
    while (celulas.length % 7 !== 0) celulas.push(null);

    const semanas = [];
    for (let i = 0; i < celulas.length; i += 7) semanas.push(celulas.slice(i, i + 7));
    return semanas;
}

function ResumoFonte({ fonte, totalDias, semana }) {
    const semDados = fonte.diasSemDados > 0;
    const nenhum = fonte.diasComDados === 0;

    return (
        <div className={`rounded-lg border px-3 py-2.5 ${nenhum
            ? "border-destructive/30 bg-destructive/5"
            : semDados ? "border-amber-500/30 bg-amber-500/5" : "border-success/30 bg-success/5"}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{fonte.nome}</p>
            <p className="mt-0.5 text-lg font-bold tabular-nums text-foreground">
                {fonte.diasComDados}
                <span className="text-xs font-medium text-muted-foreground"> / {totalDias} dias</span>
            </p>
            <p className="text-[10px] text-muted-foreground">
                {nenhum
                    ? "Nenhuma importação no período"
                    : <>{fmtNumero(fonte.totalRegistros)} registros · último dia {fmtDia(fonte.ultimoDiaComDados)}</>}
            </p>
            {semDados && !nenhum && (
                <p className="mt-0.5 text-[10px] font-semibold text-amber-600">
                    <i className="fa fa-exclamation-triangle mr-1" />
                    {fonte.diasSemDados} dia(s) sem dados {semana ? "em dia útil" : "(outros relatórios têm)"}
                </p>
            )}
        </div>
    );
}

/** Uma linha dentro do dia: sigla do relatório e a quantidade importada (ou "sem dados"). */
function LinhaFonte({ fonte, quantidade, pendente, onAbrir }) {
    if (quantidade > 0) {
        return (
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAbrir(fonte.codigo); }}
                title={`${fonte.nome}: ${fmtNumero(quantidade)} registro(s) - clique para ver`}
                className="flex w-full items-center justify-between rounded border border-success/30 bg-success/10 px-1.5 py-px text-[10px] font-semibold text-success hover:bg-success/20"
            >
                <span>{SIGLA[fonte.codigo] ?? fonte.nome}</span>
                <span className="tabular-nums">{fmtNumero(quantidade)}</span>
            </button>
        );
    }
    if (pendente) {
        return (
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAbrir(fonte.codigo); }}
                title={`${fonte.nome}: sem dados importados neste dia`}
                className="flex w-full items-center justify-between rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-px text-[10px] font-semibold text-amber-600 hover:bg-amber-500/20"
            >
                <span>{SIGLA[fonte.codigo] ?? fonte.nome}</span>
                <span>sem dados</span>
            </button>
        );
    }
    return null;
}

function CelulaDia({ dia, fontes, autorizado, destacar, onAbrir }) {
    const fimDeSemana = FIM_DE_SEMANA.includes(dia.diaSemana);
    const linhas = fontes.map(f => ({
        fonte: f,
        quantidade: dia.quantidades[f.codigo] ?? 0,
        pendente: estaPendente(autorizado, dia, f.codigo),
    }));
    const temPendencia = linhas.some(l => l.pendente);
    const temDados = linhas.some(l => l.quantidade > 0);
    const [, mes, numero] = dia.data.split("-");
    const primeiroDoMes = numero === "01";

    // clicar no dia abre o primeiro relatório com dados (ou o primeiro da lista); clicar numa sigla abre aquele
    const abrirDia = () => onAbrir(dia, (linhas.find(l => l.quantidade > 0) ?? linhas[0]).fonte.codigo);

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={abrirDia}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); abrirDia(); } }}
            title="Ver os dados deste dia"
            className={`min-h-[92px] cursor-pointer rounded-lg border p-1.5 transition hover:ring-2 hover:ring-primary/30 ${temPendencia
            ? "border-amber-500/40 bg-amber-500/5"
            : temDados ? "border-success/30 bg-card" : "border-border/60 bg-muted/30"} ${destacar ? "opacity-100" : "opacity-30"}`}>
            <div className="mb-1 flex items-center justify-between">
                <span className={`text-xs font-bold tabular-nums ${fimDeSemana ? "text-muted-foreground" : "text-foreground"}`}>
                    {numero}
                    {primeiroDoMes && <span className="ml-1 text-[9px] font-semibold text-primary">/{mes}</span>}
                </span>
                {temPendencia && <i className="fa fa-exclamation-triangle text-[9px] text-amber-600" />}
            </div>
            <div className="space-y-0.5">
                {linhas.map(l => (
                    <LinhaFonte key={l.fonte.codigo} fonte={l.fonte} quantidade={l.quantidade} pendente={l.pendente}
                        onAbrir={(codigo) => onAbrir(dia, codigo)} />
                ))}
            </div>
        </div>
    );
}

function BlocoFilial({ filial }) {
    const [somentePendencias, setSomentePendencias] = useState(false);
    const [selecao, setSelecao] = useState(null); // { dia, fonte }
    const autorizado = filial.tipoDistribuidor === "AUTORIZADO";

    const semanas = useMemo(() => montarSemanas(filial.dias), [filial]);

    const diaTemPendencia = (dia) => filial.fontes.some(f => estaPendente(autorizado, dia, f.codigo));
    const totalPendencias = useMemo(
        () => filial.dias.filter(d => filial.fontes.some(f => estaPendente(autorizado, d, f.codigo))).length,
        [filial, autorizado]
    );

    return (
        <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border border-border bg-card shadow-card"
        >
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                    <i className="fa fa-building text-primary" />
                    <h2 className="text-sm font-bold text-foreground">{filial.filialNome}</h2>
                    <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                        {autorizado ? "Distribuidor Autorizado (DA)" : "Distribuidor Logístico (DL)"}
                    </span>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <input
                        type="checkbox"
                        checked={somentePendencias}
                        onChange={e => setSomentePendencias(e.target.checked)}
                    />
                    Destacar só dias com pendência ({totalPendencias})
                </label>
            </header>

            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
                {filial.fontes.map(f => (
                    <ResumoFonte key={f.codigo} fonte={f} totalDias={filial.dias.length} semana={autorizado} />
                ))}
            </div>

            <div className="px-4 pb-4">
                <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
                    {filial.fontes.map(f => (
                        <span key={f.codigo}>
                            <strong className="text-foreground">{SIGLA[f.codigo] ?? f.nome}</strong> = {f.nome}
                        </span>
                    ))}
                    <span className="inline-flex items-center gap-1">
                        <span className="h-2 w-2 rounded-sm border border-success/40 bg-success/20" /> importado
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <span className="h-2 w-2 rounded-sm border border-amber-500/50 bg-amber-500/20" /> sem dados
                    </span>
                </div>

                <div className="overflow-x-auto">
                    <div className="min-w-[640px]">
                        <div className="mb-1 grid grid-cols-7 gap-1.5">
                            {SEMANA.map((nome, i) => (
                                <div
                                    key={nome}
                                    className={`text-center text-[10px] font-semibold uppercase tracking-wider ${i === 0 || i === 6 ? "text-muted-foreground/70" : "text-muted-foreground"}`}
                                >
                                    {nome}
                                </div>
                            ))}
                        </div>

                        <div className="space-y-1.5">
                            {semanas.map((semana, i) => (
                                <div key={i} className="grid grid-cols-7 gap-1.5">
                                    {semana.map((dia, j) => dia
                                        ? <CelulaDia
                                            key={dia.data}
                                            dia={dia}
                                            fontes={filial.fontes}
                                            autorizado={autorizado}
                                            destacar={!somentePendencias || diaTemPendencia(dia)}
                                            onAbrir={(d, fonte) => setSelecao({ dia: d, fonte })}
                                        />
                                        : <div key={`vazio-${i}-${j}`} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {selecao && (
                <DetalheDia
                    filial={filial}
                    autorizado={autorizado}
                    dia={selecao.dia}
                    fonteInicial={selecao.fonte}
                    onFechar={() => setSelecao(null)}
                />
            )}
        </motion.section>
    );
}

const fmtCelula = (coluna, valor) => {
    if (valor === null || valor === undefined || valor === "") return "—";
    if (coluna.tipo === "booleano") {
        return valor
            ? <span className="font-semibold text-success">Sim</span>
            : <span className="text-muted-foreground">Não</span>;
    }
    if (coluna.tipo === "moeda") return Number(valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    if (coluna.tipo === "numero") return Number(valor).toLocaleString("pt-BR", { maximumFractionDigits: 3 });
    return valor;
};

/** Registros importados num dia: um seletor de relatório e a tabela dos dados. */
function DetalheDia({ filial, autorizado, dia, fonteInicial, onFechar }) {
    const service = useMemo(() => new ComissaoService(), []);
    const [fonte, setFonte] = useState(fonteInicial);
    const [detalhe, setDetalhe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState("");

    useEffect(() => {
        let ativo = true;
        setLoading(true);
        setErro("");
        service.detalheDiaImportado({ fonte, data: dia.data, filialId: filial.filialId })
            .then(res => { if (ativo) setDetalhe(res.data); })
            .catch(e => { if (ativo) { setDetalhe(null); setErro(e?.response?.data?.message || "Erro ao consultar os dados do dia."); } })
            .finally(() => { if (ativo) setLoading(false); });
        return () => { ativo = false; };
    }, [fonte, dia.data, filial.filialId, service]);

    const colunas = useMemo(() => (detalhe?.colunas ?? []).map(c => ({
        key: c.chave,
        label: c.rotulo,
        sortable: true,
        center: c.tipo === "numero" || c.tipo === "moeda" || c.tipo === "booleano",
        render: (valor) => fmtCelula(c, valor),
    })), [detalhe]);

    const [ano, mes, numero] = dia.data.split("-");

    return (
        <Dialog
            visible
            modal
            blockScroll
            onHide={onFechar}
            className="w-[95%] max-w-[1200px]"
            contentClassName="p-0"
            header={
                <div>
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className="fa fa-calendar-day text-primary" />
                        {filial.filialNome} · {numero}/{mes}/{ano}
                        <span className="text-xs font-medium capitalize text-muted-foreground">({dia.diaSemana})</span>
                    </div>
                </div>
            }
        >
            <div className="space-y-3 p-4">
                <div className="flex flex-wrap gap-1.5">
                    {filial.fontes.map(f => {
                        const qtd = dia.quantidades[f.codigo] ?? 0;
                        const pendente = estaPendente(autorizado, dia, f.codigo);
                        const ativa = f.codigo === fonte;
                        return (
                            <button
                                key={f.codigo}
                                type="button"
                                onClick={() => setFonte(f.codigo)}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${ativa
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : pendente
                                        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20"
                                        : qtd > 0
                                            ? "border-success/30 bg-success/10 text-success hover:bg-success/20"
                                            : "border-border bg-muted text-muted-foreground hover:bg-muted/70"}`}
                            >
                                {f.nome}
                                <span className="tabular-nums opacity-80">{qtd > 0 ? fmtNumero(qtd) : pendente ? "sem dados" : "0"}</span>
                            </button>
                        );
                    })}
                </div>

                {erro && (
                    <p className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                        <i className="fa fa-exclamation-triangle mr-1.5" />
                        {erro}
                    </p>
                )}

                {!erro && detalhe && detalhe.truncado && (
                    <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700">
                        <i className="fa fa-info-circle mr-1.5" />
                        Mostrando os primeiros {fmtNumero(detalhe.limite)} de {fmtNumero(detalhe.total)} registros do dia.
                    </p>
                )}

                {!erro && detalhe && !loading && detalhe.linhas.length === 0 && (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                        <i className="fa fa-inbox mr-1.5" />
                        Nenhum registro de {detalhe.fonteNome} importado neste dia.
                    </p>
                )}

                {!erro && (loading || (detalhe && detalhe.linhas.length > 0)) && (
                    <CustomTable
                        data={detalhe?.linhas ?? []}
                        columns={colunas}
                        loading={loading}
                        pagination
                        pageSize={20}
                        exportable
                        exportFilename={`${filial.filialNome} - ${fonte} - ${dia.data}`}
                        dense
                        className="text-[11px]"
                    />
                )}
            </div>
        </Dialog>
    );
}

/**
 * Calendário da competência com os dias em que cada relatório teve dados importados (Escalas, Greenmile, SAP
 * entregas e retornos para o distribuidor logístico; pedidos para o autorizado), por filial.
 */
export default function TabDiasImportados({ ano, mes, filialId }) {
    const service = useMemo(() => new ComissaoService(), []);
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState("");

    useEffect(() => {
        setLoading(true);
        setErro("");
        service.diasImportados({ ano, mes, filialId })
            .then(res => setDados(res.data))
            .catch(e => setErro(e?.response?.data?.message || "Erro ao consultar os dias importados."))
            .finally(() => setLoading(false));
    }, [ano, mes, filialId, service]);

    return (
        <div className="space-y-4 p-4">
            <div>
                <h2 className="text-sm font-bold text-foreground">Dias com dados importados</h2>
                <p className="text-xs text-muted-foreground">
                    {dados ? <>Competência de {fmtData(dados.inicio)} a {fmtData(dados.fim)}. </> : null}
                    Cada dia mostra a quantidade de registros importados por relatório. "Sem dados" aponta um relatório
                    sem registros em um dia em que outro da mesma filial tem (no DA, dia útil sem pedidos).
                </p>
            </div>

            {loading && (
                <p className="py-10 text-center text-sm text-muted-foreground">
                    <i className="fa fa-spinner fa-spin mr-2" />
                    Consultando importações...
                </p>
            )}

            {erro && (
                <p className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                    <i className="fa fa-exclamation-triangle mr-1.5" />
                    {erro}
                </p>
            )}

            {!loading && !erro && dados?.filiais?.length === 0 && (
                <p className="py-10 text-center text-sm text-muted-foreground">
                    Nenhuma filial teve dados importados nesta competência.
                </p>
            )}

            {!loading && !erro && dados?.filiais?.map(f => <BlocoFilial key={f.filialId} filial={f} />)}
        </div>
    );
}
